"use client";

import { useEffect, useRef, useState } from "react";
import {
    Sun,
    Moon,
    LogOut,
    User,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    ShieldCheck,
    Mail,
    Zap,
    X,
    Activity,
    Search,
    Clock,
    BarChart3,
    Volume2,
    VolumeX,
    Bell
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";
import { useUser } from "@/hooks/useUser";
import { useSocket } from "./providers/socket-provider";
import { useCampaignHook } from "@/hooks/useCampaignHook";
import { useContacts } from "@/hooks/useContacts";
import { useListHook } from "@/hooks/useListHook";
import {
    LayoutDashboard,
    Users,
    List,
    Megaphone,
    Archive,
    FileText,
    Settings
} from "lucide-react"
import { getAudioContext, playBoostedSound } from "@/utils/sound";

const statConfigs: Record<string, { label: string; icon: any; color: string; bgColor: string; borderColor: string }> = {
    total: {
        label: "Total Emails",
        icon: Mail,
        color: "text-blue-600 dark:text-blue-400",
        bgColor: "bg-blue-50 dark:bg-blue-900/20",
        borderColor: "border-blue-100 dark:border-blue-800/30",
    },
    successCount: {
        label: "Processed",
        icon: Activity,
        color: "text-indigo-600 dark:text-indigo-400",
        bgColor: "bg-indigo-50 dark:bg-indigo-900/20",
        borderColor: "border-indigo-100 dark:border-indigo-800/30",
    },
    failedCount: {
        label: "Failed",
        icon: XCircle,
        color: "text-rose-600 dark:text-rose-400",
        bgColor: "bg-rose-50 dark:bg-rose-900/20",
        borderColor: "border-rose-100 dark:border-rose-800/30",
    },
    safeCount: {
        label: "Safe",
        icon: ShieldCheck,
        color: "text-emerald-600 dark:text-emerald-400",
        bgColor: "bg-emerald-50 dark:bg-emerald-900/20",
        borderColor: "border-emerald-100 dark:border-emerald-800/30",
    },
    unsafeCount: {
        label: "Unsafe",
        icon: AlertTriangle,
        color: "text-amber-600 dark:text-amber-400",
        bgColor: "bg-amber-50 dark:bg-amber-900/20",
        borderColor: "border-amber-100 dark:border-amber-800/30",
    },
    invalidCount: {
        label: "Invalid",
        icon: XCircle,
        color: "text-red-600 dark:text-red-400",
        bgColor: "bg-red-50 dark:bg-red-900/20",
        borderColor: "border-red-100 dark:border-red-800/30",
    },
    riskyCount: {
        label: "Risky",
        icon: Zap,
        color: "text-purple-600 dark:text-purple-400",
        bgColor: "bg-purple-50 dark:bg-purple-900/20",
        borderColor: "border-purple-100 dark:border-purple-800/30",
    },
};

export const routeConfig = {
    analysis: {
        label: "Analytics Dashboard",
        description: "View key performance metrics, campaign insights, and system analytics.",
        shortDescription: "Analytics & performance overview",
        icon: LayoutDashboard,
        emptyTitle: "No analytics data available",
        emptyDescription: "Data will appear here once campaigns and contacts start generating activity.",
        showInSidebar: true,
        permission: "view_analysis",
    },

    contacts: {
        label: "Contacts",
        description: "Manage your contact database, segments, and engagement history.",
        shortDescription: "Manage your audience",
        icon: Users,
        emptyTitle: "No contacts yet",
        emptyDescription: "Start building your audience by adding your first contact.",
        showInSidebar: true,
        permission: "manage_contacts",
    },

    lists: {
        label: "Lists",
        description: "Organize contacts into targeted groups for campaigns.",
        shortDescription: "Contact segmentation",
        icon: List,
        emptyTitle: "No lists created",
        emptyDescription: "Create lists to segment your audience for better targeting.",
        showInSidebar: true,
        permission: "manage_lists",
    },

    campaigns: {
        label: "Campaigns",
        description: "Create, send, and track your email marketing campaigns.",
        shortDescription: "Email campaign management",
        icon: Megaphone,
        emptyTitle: "No campaigns yet",
        emptyDescription: "Launch your first campaign to start reaching your audience.",
        showInSidebar: true,
        permission: "manage_campaigns",
    },

    archive: {
        label: "Archive",
        description: "View and restore archived campaigns, contacts, and templates.",
        shortDescription: "Archived records",
        icon: Archive,
        emptyTitle: "Archive is empty",
        emptyDescription: "Archived items will appear here once you move them from active sections.",
        showInSidebar: true,
        permission: "view_archive",
    },

    templates: {
        label: "Templates",
        description: "Design and manage reusable email templates.",
        shortDescription: "Reusable email layouts",
        icon: FileText,
        emptyTitle: "No templates available",
        emptyDescription: "Create your first template to streamline campaign creation.",
        showInSidebar: true,
        permission: "manage_templates",
    },

    settings: {
        label: "Settings",
        description: "Configure system preferences, roles, and master data.",
        shortDescription: "System configuration",
        icon: Settings,
        emptyTitle: "No configuration found",
        emptyDescription: "System settings and master data will be displayed here.",
        showInSidebar: true,
        permission: "manage_settings",
    },
}

export function DashboardHeader() {
    const router = useRouter();
    const { logoutHandler } = useAuth();
    const { user } = useUser();
    const userId = user?._id;
    const { socket, isConnected } = useSocket();
    const { fetchContacts, refreshContacts } = useContacts();
    const { fetchRecipientLists, clearRevalidatingList } = useListHook()


    const [theme, setTheme] = useState<"light" | "dark">("light");
    const [logs, setLogs] = useState<any[]>([]);
    const [open, setOpen] = useState(false);
    const [stats, setStats] = useState<any>(null);
    const [soundEnabled, setSoundEnabled] = useState<boolean | null>(null);
    const [showSoundModal, setShowSoundModal] = useState(false);
    const [action, setAction] = useState<string | null>('1st_time');

    const pathname: string = usePathname() || "";

    // Get last segment
    const segment = pathname.split("/").filter(Boolean).pop()

    const currentRoute: any =
        segment && routeConfig[segment as keyof typeof routeConfig]

    const Icon = currentRoute?.icon

    const IST = () =>
        new Date().toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
        });

    const soundsRef = useRef({
        success: null as HTMLAudioElement | null,
        error: null as HTMLAudioElement | null,
    });

    useEffect(() => {
        soundsRef.current.success = new Audio("/sounds/success.mp3");
        soundsRef.current.error = new Audio("/sounds/error_sound.wav");

        Object.values(soundsRef.current).forEach((audio) => {
            if (audio) {
                audio.volume = 1;
                audio.load();
            }
        });
    }, []);

    useEffect(() => {
        const updateSoundEnabled = () => {
            const enabled = localStorage.getItem("sound_enabled");
            if (enabled === null) {
                setShowSoundModal(true);
            } else {
                setSoundEnabled(enabled === "true");
            }
        };

        updateSoundEnabled();

        window.addEventListener('sound_settings_changed', updateSoundEnabled);
        return () => window.removeEventListener('sound_settings_changed', updateSoundEnabled);
    }, []);

    // const handleEnableSound = async () => {
    //     try {
    //         for (const audio of Object.values(soundsRef.current)) {
    //             if (audio) {
    //                 await audio.play();
    //                 audio.pause();
    //                 audio.currentTime = 0;
    //             }
    //         }
    //         localStorage.setItem("sound_enabled", "true");
    //         setSoundEnabled(true);
    //         setShowSoundModal(false);
    //     } catch (error) {
    //         console.error("Audio unlock failed", error);
    //     }
    // };

    const handleEnableSound = async () => {
        try {
            const ctx = getAudioContext();
            if (!ctx) return;

            if (ctx.state === "suspended") {
                await ctx.resume();
            }

            // 🔊 Play test sound immediately (best place)
            const successSound = localStorage.getItem("success_sound") || "success.mp3";
            if (successSound !== "none") playBoostedSound(`/sounds/${successSound}`);

            localStorage.setItem("sound_enabled", "true");
            setSoundEnabled(true);
            setShowSoundModal(false);
            window.dispatchEvent(new Event('sound_settings_changed'));

        } catch (error) {
            console.error("Audio unlock failed", error);
        }
    };

    const handleSkipSound = () => {
        localStorage.setItem("sound_enabled", "false");
        setSoundEnabled(false);
        setShowSoundModal(false);
        window.dispatchEvent(new Event('sound_settings_changed'));
    };

    const toggleSound = () => {
        const newVal = !soundEnabled;
        setSoundEnabled(newVal);
        localStorage.setItem("sound_enabled", String(newVal));
        window.dispatchEvent(new Event('sound_settings_changed'));
    };

    // Theme
    useEffect(() => {
        const saved = localStorage.getItem("theme") as "light" | "dark" | null;
        if (saved) {
            setTheme(saved);
            document.documentElement.classList.toggle("dark", saved === "dark");
        }
    }, []);

    const toggleTheme = () => {
        const newTheme = theme === "light" ? "dark" : "light";
        setTheme(newTheme);
        localStorage.setItem("theme", newTheme);
        document.documentElement.classList.toggle("dark", newTheme === "dark");
    };

    // Join socket room
    useEffect(() => {
        if (socket && isConnected && userId) {
            socket.emit("joinRoom", userId);
        }
    }, [socket, isConnected, userId]);

    // Socket listener
    useEffect(() => {
        if (socket && isConnected) {
            socket.on("email_processing", (data) => {
                const newLog = {
                    message: data.message,
                    status: data.status,
                    timestamp: data.timestamp || IST(),
                };

                setLogs((prev) => {
                    const updated = [newLog, ...prev].slice(0, 50);
                    sessionStorage.setItem("email_logs", JSON.stringify(updated));
                    return updated;
                });

                if (data.metadata) {
                    setStats(data.metadata);
                    sessionStorage.setItem(
                        "email_stats",
                        JSON.stringify(data.metadata)
                    );
                }
                const isSoundEnabled =
                    localStorage.getItem("sound_enabled") === "true";

                if (data.status === "completed") {
                    refreshContacts();
                    if (isSoundEnabled) {
                        const successSound = localStorage.getItem("success_sound") || "success.mp3";
                        if (successSound !== "none") playBoostedSound(`/sounds/${successSound}`);
                    }
                    if (data.listId) {
                        clearRevalidatingList(data.listId);
                        fetchRecipientLists();
                    }
                }

                if (data.status === "failed") {
                    refreshContacts();
                    if (isSoundEnabled) {
                        const errorSound = localStorage.getItem("error_sound") || "error_sound.wav";
                        if (errorSound !== "none") playBoostedSound(`/sounds/${errorSound}`);
                    }
                    if (data.listId) {
                        clearRevalidatingList(data.listId);
                        fetchRecipientLists();
                    }
                    // fetchContacts();
                }

                sessionStorage.setItem("email_viewed", "false");
            });
        }
    }, [socket, isConnected]);

    // Restore
    useEffect(() => {
        const storedLogs = sessionStorage.getItem("email_logs");
        const storedStats = sessionStorage.getItem("email_stats");

        if (storedLogs) setLogs(JSON.parse(storedLogs));
        if (storedStats) setStats(JSON.parse(storedStats));
    }, []);

    const currentLog = logs[0];

    const isCompleted = currentLog?.status === "completed";
    const isProcessing =
        currentLog?.status === "processing" ||
        currentLog?.status === "start_processing";

    const isViewed =
        typeof window !== "undefined" &&
        sessionStorage.getItem("email_viewed") === "true";

    const statusColorMap: Record<string, string> = {
        start_processing:
            "bg-blue-100/60 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        processing:
            "bg-indigo-100/60 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
        completed:
            "bg-green-100/60 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        failed:
            "bg-red-100/60 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    };

    const handleClose = () => {
        fetchRecipientLists();
        refreshContacts();
        setOpen(false);
        sessionStorage.setItem("email_viewed", "true");
        sessionStorage.removeItem("email_logs");
        sessionStorage.removeItem("email_stats");
    };

    return (
        <>
            {/* HEADER */}
            <header className="h-16 sticky top-0 z-40 flex items-center justify-between px-6 backdrop-blur-xl bg-white/60 dark:bg-gray-900/60 border-b border-white/20 shadow-sm">

                {/* LEFT */}
                <div className="flex items-start gap-3">
                    {Icon && (
                        <Icon className="h-5 w-5 mt-4 text-gray-600 dark:text-gray-300 " />
                    )}

                    <div className="flex flex-col">
                        <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                            {currentRoute?.label ?? "Dashboard"}
                        </h1>

                        {currentRoute?.shortDescription && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-tight">
                                {currentRoute.shortDescription}
                            </p>
                        )}
                    </div>
                </div>

                {/* RIGHT */}
                <div className="flex items-center gap-4">

                    {/* 🔥 LOG CHIP */}
                    <AnimatePresence>
                        {logs.length > 0 && !isViewed && (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                className="flex items-center gap-3"
                            >
                                <div
                                    className={`
                                        pl-3 pr-4 py-1.5 rounded-full text-[11px] font-semibold
                                        backdrop-blur-xl border border-white/20 shadow-lg
                                        flex items-center gap-2
                                        ${isProcessing ? "animate-pulse" : ""}
                                        ${statusColorMap[currentLog?.status] || "bg-gray-200"}
                                    `}
                                >
                                    <div className="flex items-center justify-center">
                                        {isProcessing && <Activity className="h-3 w-3 animate-spin duration-[3s]" />}
                                        {isCompleted && <CheckCircle2 className="h-3 w-3" />}
                                        {currentLog?.status === 'failed' && <XCircle className="h-3 w-3" />}
                                    </div>
                                    <span className="truncate max-w-[180px]">{currentLog?.message}</span>
                                </div>

                                {isCompleted && !isViewed && (
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => setOpen(true)}
                                        className="px-4 py-1.5 text-[11px] font-bold rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all"
                                    >
                                        View Report
                                    </motion.button>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* SOUND TOGGLE */}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleSound}
                        title={soundEnabled ? "Mute sounds" : "Unmute sounds"}
                    >
                        {soundEnabled ? (
                            <Volume2 className="h-5 w-5 text-indigo-500" />
                        ) : (
                            <VolumeX className="h-5 w-5 text-gray-400" />
                        )}
                    </Button>

                    {/* THEME */}
                    <Button variant="ghost" size="icon" onClick={toggleTheme}>
                        {theme === "light" ? <Moon /> : <Sun />}
                    </Button>

                    {/* USER */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <div className="cursor-pointer">
                                <Avatar className="ring-2 ring-white/30 dark:ring-gray-700 hover:scale-105 transition">
                                    <AvatarImage src="/avatar.png" />
                                    <AvatarFallback>
                                        {user?.name?.split(" ").map((n) => n[0]).join("")}
                                    </AvatarFallback>
                                </Avatar>
                            </div>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                            align="end"
                            className="
                            w-48 backdrop-blur-xl bg-white/70 dark:bg-gray-900/70
                            border border-white/20 dark:border-gray-800
                            shadow-xl rounded-xl
                            "
                        >
                            <DropdownMenuItem
                                className="flex items-center gap-2 cursor-pointer hover:bg-white/40 dark:hover:bg-gray-800/40"
                                onClick={() => router.push("/account")}
                            >
                                <User className="h-4 w-4" />
                                Account
                            </DropdownMenuItem>

                            <DropdownMenuItem
                                onSelect={(e) => logoutHandler()}
                                className="hover:bg-white/40 dark:hover:bg-gray-800/40"
                            >
                                <div className="flex items-center gap-2 cursor-pointer text-red-500">
                                    <LogOut className="h-4 w-4" />
                                    Logout
                                </div>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            {/* MODAL */}
            <AnimatePresence>
                {open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="w-[520px] max-h-[85vh] flex flex-col rounded-3xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="p-6 pb-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800">
                                <div>
                                    <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400">
                                        Verification Report
                                    </h2>
                                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5 mt-0.5">
                                        <AlertTriangle className="h-3 w-3" />
                                        Single viewing session only
                                    </p>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-900 dark:hover:text-white"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                                {/* STATS GRID */}
                                {stats && (
                                    <div className="grid grid-cols-2 gap-4 mb-8">
                                        {Object.entries(stats).map(([key, val], index) => {
                                            const config = statConfigs[key] || {
                                                label: key.replace(/([A-Z])/g, " $1"),
                                                icon: Activity,
                                                color: "text-gray-600 dark:text-gray-400",
                                                bgColor: "bg-gray-50 dark:bg-gray-800/50",
                                                borderColor: "border-gray-100 dark:border-gray-700/50",
                                            };
                                            const Icon = config.icon;

                                            return (
                                                <motion.div
                                                    key={key}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                    className={`
                                                        p-4 rounded-2xl border transition-all hover:shadow-md
                                                        ${config.bgColor} ${config.borderColor}
                                                    `}
                                                >
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className={`p-2 rounded-xl bg-white dark:bg-gray-900 shadow-sm ${config.color}`}>
                                                            <Icon className="h-4 w-4" />
                                                        </div>
                                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 capitalize">
                                                            {config.label}
                                                        </span>
                                                    </div>
                                                    <div className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                                                        {val as any}
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* LOGS SECTION */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                            Activity Logs
                                        </h3>
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                                            {logs.length} entries
                                        </span>
                                    </div>

                                    <div className="space-y-2.5">
                                        {logs.map((log, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.3 + (i * 0.02) }}
                                                className="group relative flex gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors border border-transparent hover:border-gray-100 dark:hover:border-gray-800"
                                            >
                                                <div className="flex flex-col items-center">
                                                    <div className={`h-2 w-2 rounded-full mt-1.5 ${log.status === 'completed' ? 'bg-green-500' :
                                                        log.status === 'failed' ? 'bg-red-500' :
                                                            'bg-blue-500'
                                                        }`} />
                                                    {i !== logs.length - 1 && <div className="w-[1px] flex-1 bg-gray-100 dark:bg-gray-800 mt-2" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-md text-gray-700 dark:text-gray-300 leading-relaxed break-words">
                                                        {log.message}
                                                    </p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <Clock className="h-3 w-3 text-gray-400 font-light" />
                                                        <span className="text-[12px] text-gray-400 font-medium">{log.timestamp}</span>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 pt-2">
                                <Button
                                    onClick={handleClose}
                                    variant="destructive"
                                    className="w-full h-11 rounded-xl font-semibold shadow-lg shadow-red-500/10"
                                >
                                    Dismiss Report
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* SOUND PERMISSION MODAL */}
            <AnimatePresence>
                {showSoundModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-lg px-4">

                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 40, scale: 0.96 }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            className="relative w-full max-w-[420px]"
                        >
                            {/* Glow Effect */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#07beff]/30 via-indigo-400/20 to-purple-400/20 blur-2xl opacity-60 rounded-[28px]" />

                            {/* Card */}
                            <div className="relative bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border border-white/20 dark:border-zinc-800 rounded-[28px] shadow-2xl p-8 text-center">

                                {/* Animated Icon */}
                                <motion.div
                                    initial={{ scale: 0.7, rotate: -15, opacity: 0 }}
                                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                                    transition={{ type: "spring", stiffness: 120 }}
                                    className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#07beff]/10 flex items-center justify-center"
                                >
                                    <motion.div
                                        animate={{
                                            rotate: [0, 10, -8, 6, 0],
                                            scale: [1, 1.1, 1],
                                        }}
                                        transition={{
                                            duration: 1.2,
                                            repeat: Infinity,
                                            repeatDelay: 2, // 🔥 gives breathing gap
                                            ease: "easeInOut",
                                        }}
                                    >
                                        <Bell className="h-8 w-8 text-[#07beff]" />
                                    </motion.div>
                                </motion.div>

                                {/* Title */}
                                <h2 className="text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
                                    Enable Sound Alerts
                                </h2>

                                {/* Subtitle */}
                                <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed mb-8">
                                    Stay updated with real-time campaign activity, email verification, and important notifications.
                                </p>

                                {/* Buttons */}
                                <div className="flex flex-col gap-3">
                                    <Button
                                        onClick={handleEnableSound}
                                        className="w-full h-12 rounded-xl bg-[#07beff] hover:bg-[#06a6dd] text-white font-semibold transition-all shadow-lg shadow-[#07beff]/20 hover:scale-[1.02]"
                                    >
                                        Enable Sound
                                    </Button>

                                    <Button
                                        variant="ghost"
                                        onClick={handleSkipSound}
                                        className="w-full h-12 rounded-xl text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all"
                                    >
                                        {action === "1st_time" ? "Skip for Now" : "Disable Sound"}
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
}